<template>
  <div class="column">
    <woot-modal-header :header-title="$t('AUTOMATION.EDIT.TITLE')" />

    <woot-loading-state
      v-if="isFetching"
      :message="$t('AUTOMATION.EDIT.LOADING')"
    />

    <template v-else>
      <div class="steps-indicator">
        <div :class="['step', { active: currentStep === 1 }]">1</div>
        <div class="line"></div>
        <div :class="['step', { active: currentStep === 2 }]">2</div>
        <div class="line"></div>
        <div :class="['step', { active: currentStep === 3 }]">3</div>
      </div>

      <div class="row modal-content">
        <div v-if="automation" class="medium-12 columns">
          <template v-if="currentStep === 1">
            <woot-input
              v-model="automation.name"
              :label="$t('AUTOMATION.ADD.FORM.NAME.LABEL')"
              type="text"
              :class="{ error: $v.automation.name.$error }"
              :error="
                $v.automation.name.$error
                  ? $t('AUTOMATION.ADD.FORM.NAME.ERROR')
                  : ''
              "
              :placeholder="$t('AUTOMATION.ADD.FORM.NAME.PLACEHOLDER')"
              @blur="$v.automation.name.$touch"
            />

            <woot-input
              v-model="automation.description"
              :label="$t('AUTOMATION.ADD.FORM.DESC.LABEL')"
              type="text"
              :class="{ error: $v.automation.description.$error }"
              :error="
                $v.automation.description.$error
                  ? $t('AUTOMATION.ADD.FORM.DESC.ERROR')
                  : ''
              "
              :placeholder="$t('AUTOMATION.ADD.FORM.DESC.PLACEHOLDER')"
              @blur="$v.automation.description.$touch"
            />

            <div class="event_wrapper">
              <label :class="{ error: $v.automation.event_name.$error }">
                {{ $t('AUTOMATION.ADD.FORM.EVENT.LABEL') }}
                <select
                  v-model="automation.event_name"
                  @change="onEventChange()"
                >
                  <option
                    v-for="event in triggerCategoryEvents"
                    :key="event.key"
                    :value="event.key"
                  >
                    {{
                      $t(
                        `INTEGRATION_SETTINGS.WEBHOOK.FORM.SUBSCRIPTIONS.EVENTS.${event.attributeI18nKey}`
                      )
                    }}
                  </option>
                </select>
                <span v-if="$v.automation.event_name.$error" class="message">
                  {{
                    $v.automation.event_name.belongsToTriggerCategory
                      ? $t('AUTOMATION.ADD.FORM.EVENT.ERROR')
                      : $t('AUTOMATION.ADD.FORM.EVENT.CATEGORY_ERROR')
                  }}
                </span>
              </label>

              <p v-if="hasAutomationMutated" class="info-message">
                {{ $t('AUTOMATION.FORM.RESET_MESSAGE') }}
              </p>
            </div>
          </template>

          <template v-if="currentStep === 2">
            <section>
              <label>
                {{ $t('AUTOMATION.ADD.FORM.CONDITIONS.LABEL') }}
              </label>

              <div class="medium-12 columns filters-wrap">
                <filter-input-box
                  v-for="(condition, i) in automation.conditions"
                  :key="i"
                  v-model="automation.conditions[i]"
                  :filter-attributes="getAttributes(automation.event_name)"
                  :input-type="
                    getInputType(automation.conditions[i].attribute_key)
                  "
                  :operators="
                    getOperators(automation.conditions[i].attribute_key)
                  "
                  :dropdown-values="
                    getConditionDropdownValues(
                      automation.conditions[i].attribute_key
                    )
                  "
                  :show-query-operator="i !== automation.conditions.length - 1"
                  :custom-attribute-type="
                    getCustomAttributeType(
                      automation.conditions[i].attribute_key
                    )
                  "
                  :v="$v.automation.conditions.$each[i]"
                  @resetFilter="resetFilter(i, automation.conditions[i])"
                  @removeFilter="removeFilter(i)"
                />

                <div class="filter-actions">
                  <woot-button
                    icon="add"
                    color-scheme="success"
                    variant="smooth"
                    size="small"
                    @click="appendNewCondition"
                  >
                    {{ $t('AUTOMATION.ADD.CONDITION_BUTTON_LABEL') }}
                  </woot-button>
                </div>
              </div>
            </section>
          </template>

          <template v-if="currentStep === 3">
            <section>
              <label>
                {{ $t('AUTOMATION.ADD.FORM.ACTIONS.LABEL') }}
              </label>

              <div class="medium-12 columns filters-wrap">
                <automation-action-input
                  v-for="(action, i) in automation.actions"
                  :key="i"
                  v-model="automation.actions[i]"
                  :action-types="
                    getSelectableActionTypes(automation.actions[i].action_name)
                  "
                  :dropdown-values="
                    getActionDropdownValues(automation.actions[i].action_name)
                  "
                  :show-action-input="
                    showActionInput(automation.actions[i].action_name)
                  "
                  :v="$v.automation.actions.$each[i]"
                  :initial-file-name="getFileName(action, automation.files)"
                  @resetAction="resetAction(i)"
                  @removeAction="removeAction(i)"
                />

                <div class="filter-actions">
                  <woot-button
                    icon="add"
                    color-scheme="success"
                    variant="smooth"
                    size="small"
                    @click="appendNewAction"
                  >
                    {{ $t('AUTOMATION.ADD.ACTION_BUTTON_LABEL') }}
                  </woot-button>
                </div>
              </div>
            </section>
          </template>

          <div class="medium-12 columns">
            <div class="modal-footer justify-content-end w-full">
              <woot-button
                v-if="currentStep > 1"
                class="button clear"
                @click.prevent="prevStep"
              >
                Anterior
              </woot-button>

              <woot-button v-if="currentStep < 3" @click="nextStep">
                Próximo
              </woot-button>

              <woot-button v-if="currentStep === 3" @click="submitAutomation">
                {{ $t('AUTOMATION.EDIT.SUBMIT') }}
              </woot-button>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import alertMixin from 'shared/mixins/alertMixin';
import automationMethodsMixin from 'dashboard/mixins/automations/methodsMixin';
import automationValidationsMixin from 'dashboard/mixins/automations/validationsMixin';
import filterInputBox from 'dashboard/components/widgets/FilterInput/Index.vue';
import automationActionInput from 'dashboard/components/widgets/AutomationActionInput.vue';

import {
  AUTOMATION_RULE_EVENTS,
  AUTOMATION_ACTION_TYPES,
  AUTOMATIONS,
} from './constants';
import { getAutomationTriggerCategory } from './constants/events';

export default {
  components: {
    filterInputBox,
    automationActionInput,
  },
  mixins: [alertMixin, automationMethodsMixin, automationValidationsMixin],
  props: {
    onClose: {
      type: Function,
      default: () => {},
    },
    selectedResponse: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      currentStep: 1,
      automationTypes: JSON.parse(JSON.stringify(AUTOMATIONS)),
      automationRuleEvent: AUTOMATION_RULE_EVENTS[0].key,
      automationRuleEvents: AUTOMATION_RULE_EVENTS,
      automationActionTypes: AUTOMATION_ACTION_TYPES,
      automationMutated: false,
      show: true,
      automation: null,
      showDeleteConfirmationModal: false,
      allCustomAttributes: [],
      mode: 'edit',
      isFetching: true,
    };
  },
  computed: {
    triggerCategory() {
      return getAutomationTriggerCategory(this.selectedResponse.event_name);
    },
    triggerCategoryEvents() {
      return this.sortedAutomationRuleEvents.filter(
        event => event.category === this.triggerCategory
      );
    },
    hasAutomationMutated() {
      if (
        this.automation.conditions[0].values ||
        this.automation.actions[0].action_params.length
      )
        return true;
      return false;
    },
    sortedAutomationRuleEvents() {
      return [...this.automationRuleEvents].sort((a, b) => {
        const labelA = this.$t(
          `INTEGRATION_SETTINGS.WEBHOOK.FORM.SUBSCRIPTIONS.EVENTS.${a.attributeI18nKey}`
        );

        const labelB = this.$t(
          `INTEGRATION_SETTINGS.WEBHOOK.FORM.SUBSCRIPTIONS.EVENTS.${b.attributeI18nKey}`
        );

        return labelA.localeCompare(labelB, 'pt-BR', {
          sensitivity: 'base',
        });
      });
    },
  },
  methods: {
    nextStep() {
      if (this.currentStep === 1) {
        this.$v.automation.name.$touch();
        this.$v.automation.description.$touch();

        if (
          this.$v.automation.name.$invalid ||
          this.$v.automation.description.$invalid
        ) {
          return;
        }
      }

      if (this.currentStep < 3) {
        this.currentStep++;
      }
    },
    prevStep() {
      if (this.currentStep > 1) {
        this.currentStep--;
      }
    },
  },
  async mounted() {
    const dispatches = [this.$store.dispatch('events/get')];
    if (
      this.$store.getters['accounts/isFeatureEnabledonAccount'](
        this.$store.getters.getCurrentAccountId,
        'opportunity_pipelines'
      )
    ) {
      dispatches.push(this.$store.dispatch('opportunityPipelines/get'));
    }
    await Promise.allSettled(dispatches);
    this.manifestCustomAttributes();
    this.allCustomAttributes = this.$store.getters['attributes/getAttributes'];
    this.formatAutomation(this.selectedResponse);

    if (this.automation?.actions?.length) {
      this.automation.actions = this.automation.actions.map(action => {
        const params = action.action_params;

        if (
          Array.isArray(params) &&
          params.length === 1 &&
          typeof params[0] === 'object' &&
          Object.keys(params[0]).some(k =>
            ['inbox_id', 'template_id'].includes(k)
          )
        ) {
          return {
            ...action,
            action_params: params[0],
          };
        }

        return action;
      });
    }

    this.isFetching = false;
  },
};
</script>

<style lang="scss" scoped>
.steps-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 18px;
  margin-bottom: 28px;
}

.step {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: #e5e5e5;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 16px;
}

.step.active {
  background: #2563eb;
  color: white;
}

.line {
  width: 50px;
  height: 2px;
  background: #e5e5e5;
}

.filters-wrap {
  padding: var(--space-normal);
  border-radius: var(--border-radius-large);
  border: 1px solid var(--color-border);
  background: var(--color-background-light);
  margin-bottom: var(--space-normal);
}

.filter-actions {
  margin-top: var(--space-normal);
}

.event_wrapper {
  select {
    margin: var(--space-zero);
  }

  .info-message {
    font-size: var(--font-size-mini);
    color: var(--s-500);
    text-align: right;
  }

  margin-bottom: var(--space-medium);
}
</style>
